import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import _get from "lodash/get";

import { ContentHolder } from "commons";
import { convertToMoney, convertToFractionalMonetaryUnits } from "commons/src/Money";
import { format, now, FORMAT, DAY } from "commons/src/NuDateUtils";
import { isPrintMode } from "commons/src/QueryParameter";
import { PDFWrapper, STATE as PRINT_STATE } from "commons/src/PDFWrapper";
import { isTransactionsEnabled } from "commons/src/helpers/RoleHelpers";

import { requestObject } from "../../reducers/requestsActions";
import { ErrorSupportNumber, ERROR, WARNING } from "../../helpers/ErrorHelpers";
import { Message } from "../../components/Message";
import { AccountSummary } from "./AccountSummary";
import { Investments } from "./Investments";
import { PortfolioModel } from "./PortfolioModel";
import { RegularTransactions, TransactionTypes } from "./RegularTransactions";
import { WrapperDetails } from "./WrapperDetails";
import { CSVGenerator } from "./CSVGenerator";
import { AccountLink } from "./AccountLink";
import { AccountDetail, ACCOUNT_DETAIL_TYPES } from "./AccountDetail";
import { ActionsContainer } from "./ActionsContainer";
import { PDFDownload } from "./PDFDownload";
import { PDFFrontPage } from "./PDFFrontPage";
import { getRemainingAllowance } from "../../helpers/MoneyHelpers";

const PENDING_TRADES_MESSAGE = `The information displayed on this page does not reflect updates to in progress trades and 
transactions until the following business day. This may result in differences between the 
values shown here and those on the My Portfolio page which are updated on the same day.`;

const CSV_DOWNLOAD_ERROR_MESSAGE = (
    <span>
        An error occurred when attempting to download your Portfolio/Account Summary information,
        please try again or call us on <ErrorSupportNumber />
    </span>
);

export class FolioDetailsComponent extends Component {
    state = {
        modal: null,
        showCsvDownloadError: false
    };

    clearModal = () => {
        this.setState({ modal: null });
    };

    setCsvError = newState => {
        if (newState !== this.state.showCsvDownloadError) {
            this.setState({ showCsvDownloadError: newState });
        }
    };

    handlePrint = printRoute => {
        this.props.onPrint(this.props.stateToPrint, `${this.getFileName()}.pdf`, {
            printRoute
        });
    };

    moneyIn = () => ({
        key: "Money in",
        accountPath: ["Total", "accountMap", this.props.params.accountNum],
        combinedPath: ["Total", "value"]
    });

    moneyOut = () => ({
        key: "Money out",
        accountPath: ["Total", "accountMap", this.props.params.accountNum],
        combinedPath: ["Total", "value"]
    });

    adjustments = () => ({
        key: "Adjustments",
        accountPath: ["Adjustments", "accountMap", this.props.params.accountNum],
        combinedPath: ["Adjustments", "value"]
    });

    gain = () => ({
        key: "Gain / loss",
        accountPath: ["Gain (net)", "accountMap", this.props.params.accountNum],
        combinedPath: ["Gain (net)", "value"]
    });

    loss = () => ({
        key: "Gain / loss",
        accountPath: ["Loss (net)", "accountMap", this.props.params.accountNum],
        combinedPath: ["Loss (net)", "value"]
    });

    interAccountIn = () => ({
        key: "Inter account transfers",
        accountPath: ["Inter account transfer in", "accountMap", this.props.params.accountNum],
        combinedPath: ["Inter account transfer in", "value"]
    });

    interAccountOut = () => ({
        key: "Inter account transfers",
        accountPath: ["Inter account transfer out", "accountMap", this.props.params.accountNum],
        combinedPath: ["Inter account transfer out", "value"]
    });

    byFunds = (a, b) => {
        if (a.asset.startsWith("Cash")) {
            return -1;
        } else if (b.asset.startsWith("Cash")) {
            return 1;
        } else {
            return a.asset.localeCompare(b.asset);
        }
    };

    getAccountNumber = accountNum => (accountNum !== "Combined" ? accountNum : "");

    getSummary = analysis => {
        const holdings = Object.values(analysis.HOLDINGS);
        return _get(holdings[Object.keys(holdings)[0]], "data", []).filter(
            d => d.name === this.props.params.accountNum
        )[0];
    };

    getPortfolio = (accountNum, accounts) => {
        if (accountNum === "Combined") {
            return undefined;
        }
        return accounts[accountNum].portfolio;
    };

    getAccountType = (accountNum, accounts) => {
        if (accountNum === "Combined") {
            return "Portfolio";
        }
        return accounts[accountNum].wrapperType;
    };

    getAccountStatus = (accountNum, accountDetails) => {
        if (accountNum === "Combined") {
            return "";
        }
        return accountDetails.accountStatus;
    };

    isJointAccount = (accountNum, accounts) =>
        accountNum !== "Combined" && accounts[accountNum].numberOfOwners > 1;

    isDrawdownAccount = wrapperType => drawdownDisplayTypes.includes(wrapperType);

    getJointHolderName = jointClients =>
        (jointClients || [])
            .filter(jointClient => jointClient.clientId !== this.props.clientId)
            .map(client => `${client.forename} ${client.surname}`)
            .join(", ");

    getValue = (tableElements, money) => {
        if (!tableElements) {
            return 0;
        }

        const moneyPath =
            this.props.params.accountNum === "Combined" ? money.combinedPath : money.accountPath;
        return (
            (
                Object.values(tableElements)
                    .filter(o => o && o[money.key])
                    .map(o => o[money.key])[0] || []
            )
                .filter(o => o && o[moneyPath[0]])
                .map(o => {
                    moneyPath.forEach(path => (o = o[path]));
                    return o;
                })[0] || 0
        );
    };

    hasDetails = () => {
        return (
            this.props.accounts &&
            this.props.accounts[this.props.params.accountNum] &&
            this.props.accounts[this.props.params.accountNum].details
        );
    };

    getTotalAmountFor = transactions => transactions.reduce((acc, val) => acc + val.amount, 0);

    getPortfolioModel = fundDetails => {
        const portfolio = this.getPortfolio(this.props.params.accountNum, this.props.accounts);

        if (!portfolio) {
            return null;
        }

        portfolio.funds.forEach(fund => {
            const details = fundDetails.find(e => e.isin === fund.isin);
            fund.fundFactSheetAvailable = details?.fundFactSheetAvailable || false;
        });

        return {
            "data-testid": "model-portfolio",
            type: ACCOUNT_DETAIL_TYPES.secondary,
            heroLabel: "Portfolio name:",
            heroContent: portfolio.name,
            title: isPrintMode() ? `Model portfolio: ${portfolio.name}` : "Model portfolio",
            alignToTop: true,
            content: (
                <PortfolioModel
                    portfolio={portfolio}
                    accountNum={this.props.params.accountNum}
                    requestStatus={this.props.modelPortfolioRequestStatus}
                />
            )
        };
    };

    getRegularPayments = () => {
        const data = this.hasDetails()
            ? this.props.accounts[this.props.params.accountNum].details.regularContributions
            : [];

        if (!data.length) {
            return null;
        }

        const totalAmount = this.getTotalAmountFor(data);

        return {
            "data-testid": "regular-payments",
            type: ACCOUNT_DETAIL_TYPES.secondary,
            heroLabel: "Total payments:",
            heroContent: convertToMoney(totalAmount),
            title: "Regular payments",
            pageBreakInside: data.length > 1 ? "avoid" : "auto",
            content: (
                <RegularTransactions
                    transType={"Regular payments"}
                    nextHeader={"Next payment date"}
                    data={data}
                    accountNum={this.props.params.accountNum}
                    type={TransactionTypes.payments}
                />
            )
        };
    };

    getRegularWithdrawals = () => {
        const data = this.hasDetails()
            ? this.props.accounts[this.props.params.accountNum].details.regularWithdrawals
            : [];

        if (!data.length) {
            return null;
        }

        const totalAmount = this.getTotalAmountFor(data);

        return {
            "data-testid": "regular-withdrawal",
            type: ACCOUNT_DETAIL_TYPES.secondary,
            heroLabel: "Withdrawal amount:",
            heroContent: convertToMoney(totalAmount),
            title: "Regular withdrawal",
            content: (
                <RegularTransactions
                    transType={"Regular withdrawal"}
                    nextHeader={"Next withdrawal date"}
                    data={data}
                    accountNum={this.props.params.accountNum}
                    type={TransactionTypes.withdrawals}
                />
            )
        };
    };

    getWrapperDetails = () => {
        const wrapperDetails =
            this.props?.accounts?.[this.props?.params?.accountNum]?.wrapperDetails;
        if (!wrapperDetails) {
            return null;
        }
        const accountType = this.getAccountType(this.props.params.accountNum, this.props.accounts);
        const isISA = accountType === "Isa";
        const isPension = pensionDisplayTypes.includes(accountType);
        const isDrawdown = drawdownDisplayTypes.includes(accountType);

        const {
            airdAnnualLimit,
            totalSubscriptionCTY,
            replaceableWithdrawals,
            returnedReplaceableWithdrawals,
            accountIncomeCTY
        } = wrapperDetails;

        const replaceableWithdrawalAllowance =
            replaceableWithdrawals - returnedReplaceableWithdrawals;
        const totalAllowance = airdAnnualLimit + (replaceableWithdrawalAllowance || 0);
        return {
            "data-testid": "wrapper-details",
            type: ACCOUNT_DETAIL_TYPES.secondary,
            heroLabel: isDrawdown ? "Total income:" : "Allowance remaining:",
            heroContent: isDrawdown
                ? convertToMoney(accountIncomeCTY)
                : getRemainingAllowance(totalAllowance, totalSubscriptionCTY, isPension),
            title: isDrawdown ? "Income in current tax year" : "Allowances for current tax year",
            content: (
                <WrapperDetails
                    hasMultipleISAs={this.hasMultipleISAs()}
                    hasMultiplePensions={this.hasMultiplePensions()}
                    requestStatus={this.props.accountDetailsRequestStatus}
                    accountDetails={this.props.accounts[this.props.params.accountNum]}
                    accountType={accountType}
                    isISA={isISA}
                    isPension={isPension}
                    isDrawdown={isDrawdown}
                />
            )
        };
    };

    getAccountSummary = ({ transactions, accountDetails }) => {
        const {
            params: { accountNum },
            accounts,
            analysis
        } = this.props;

        const firstTransaction = Object.values(analysis.VALUATION_VS_MIMO)?.[0]?.valVsMimo?.[
            accountNum
        ]?.[0]?.formattedEffectiveDate;

        const startDate = format(firstTransaction, FORMAT.SLASH_SEPARATED);
        const valuationDate = format(now().subtract(1, DAY), FORMAT.SLASH_SEPARATED);

        return (
            <AccountSummary
                data-testid="account-summary"
                transactions={transactions.filter(t => t.label !== "Account value")}
                isDrawdown={this.isDrawdownAccount(this.getAccountType(accountNum, accounts))}
                accountNumber={accountNum}
                accountStatus={this.getAccountStatus(accountNum, accountDetails)}
                effectiveDateRange={{ startDate, valuationDate }}
                isJointAccount={this.isJointAccount(accountNum, accounts)}
                jointHolderName={this.getJointHolderName(
                    _get(this.props, `accounts[${accountNum}]`, {}).jointClients
                )}
            />
        );
    };

    getCSVGenerator = ({ transactions, funds }) => (
        <CSVGenerator
            data-testid="csv-download-button"
            transactions={transactions}
            funds={funds}
            isCombined={this.props.isCombined}
            accountNumber={this.getAccountNumber(this.props.params.accountNum)}
            accountType={this.getAccountType(this.props.params.accountNum, this.props.accounts)}
            isJointAccount={this.isJointAccount(this.props.params.accountNum, this.props.accounts)}
            jointHolderName={this.getJointHolderName(
                _get(this.props, `accounts[${this.props.params.accountNum}]`, {}).jointClients
            )}
            setCsvError={this.setCsvError}
            fileName={this.getFileName()}
        />
    );

    getLinks = targetAccount =>
        [
            <AccountLink
                key="analysis-link"
                data-testid="analysis-link"
                label="Analysis"
                target="analysis"
                account={targetAccount}
            />,
            isTransactionsEnabled(this.props.roles) && (
                <AccountLink
                    key="transactions-link"
                    data-testid="transactions-link"
                    label="Transactions"
                    target="transactions"
                    account={targetAccount === "Combined" ? "default" : targetAccount}
                />
            )
        ].filter(Boolean);

    getPendingTradesStatus = (accountNum, accounts, summaryTotal) => {
        const hasPendingTradesThreshold = 10;
        let portfolioTotal;
        if (accountNum === "Combined") {
            portfolioTotal = Object.values(accounts).reduce(
                (sum, value) => sum + _get(value, "totalHoldings", 0),
                0
            );
        } else {
            portfolioTotal = _get(accounts[accountNum], "totalHoldings", 0);
        }
        const roundedPortfolioTotal = convertToFractionalMonetaryUnits(portfolioTotal);
        const roundedSummaryTotal = convertToFractionalMonetaryUnits(summaryTotal);
        const difference = roundedPortfolioTotal - roundedSummaryTotal;
        return Math.abs(difference) >= hasPendingTradesThreshold;
    };

    hasMultipleISAs = () => {
        const accounts = this.props.accounts;
        return (
            Object.keys(accounts).filter(
                key => this.getAccountType(accounts[key].accountNumber, accounts) === "Isa"
            ).length > 1
        );
    };

    hasMultiplePensions = () => {
        const accounts = this.props.accounts;
        return (
            Object.keys(accounts).filter(key =>
                pensionDisplayTypes.includes(
                    this.getAccountType(accounts[key].accountNumber, accounts)
                )
            ).length > 1
        );
    };

    getFileName = (date = now()) => {
        const filePrefix = this.props.isCombined
            ? `PortfolioSummary-${this.props.clientId}`
            : `AccountSummary-${this.props.params.accountNum}`;

        return `${filePrefix}-${format(date, FORMAT.UTC_DATE_ONLY)}`;
    };

    render() {
        const analysisValues = _get(
            Object.values(this.props.analysis.TRANSACTION_BREAKDOWN)[0],
            "table",
            undefined
        );
        const gainloss =
            this.getValue(analysisValues, this.gain()) ||
            this.getValue(analysisValues, this.loss());
        const moneyin = this.getValue(analysisValues, this.moneyIn());
        const moneyout = this.getValue(analysisValues, this.moneyOut());
        const interAccountIn = this.getValue(analysisValues, this.interAccountIn());
        const interAccountOut = this.getValue(analysisValues, this.interAccountOut());
        const adjustments = this.getValue(analysisValues, this.adjustments());

        const summary = this.getSummary(this.props.analysis);

        const totalValue = convertToMoney(_get(summary, "total", 0));

        const transactions = [
            { label: "Account value", value: _get(summary, "total", 0) },
            { label: "Gain / loss", value: gainloss },
            { label: "Money in", value: moneyin },
            { label: "Money out", value: moneyout },
            { label: "Inter-account transfer in", value: interAccountIn },
            { label: "Inter-account transfer out", value: interAccountOut },
            { label: "Adjustments", value: adjustments }
        ];

        const accountType = this.getAccountType(this.props.params.accountNum, this.props.accounts);

        const funds = _get(summary, "holdingsDetails", []).sort(this.byFunds);
        const accountDetails = this.props.accounts[this.props.params.accountNum];
        const hasPendingTrades = this.getPendingTradesStatus(
            this.props.params.accountNum,
            this.props.accounts,
            _get(summary, "total", 0)
        );
        const targetAccount =
            Object.keys(this.props.accounts).length === 1
                ? Object.keys(this.props.accounts)[0]
                : this.props.params.accountNum;

        const modelPortfolio = this.getPortfolioModel(funds);

        const details = [
            this.getWrapperDetails(),
            this.getRegularWithdrawals(),
            this.getRegularPayments(),
            !isPrintMode() && modelPortfolio
        ].filter(Boolean);

        return (
            <ContentHolder>
                <Message
                    severity={WARNING}
                    text={PENDING_TRADES_MESSAGE}
                    shown={hasPendingTrades}
                />

                {(this.state.showCsvDownloadError ||
                    this.props.printState === PRINT_STATE.ERROR) && (
                    <Message
                        data-testid="portfolio-download-error"
                        severity={ERROR}
                        text={CSV_DOWNLOAD_ERROR_MESSAGE}
                        marginTop="1rem"
                    />
                )}

                <PDFFrontPage accountNum={this.props.params.accountNum} />

                <OverviewContainer data-testid="folio-overview-container">
                    <AccountDetail
                        data-testid="account-overview"
                        type={ACCOUNT_DETAIL_TYPES.primary}
                        title={`${accountType} ${this.getAccountNumber(
                            this.props.params.accountNum
                        )}`}
                        subtitle={accountDetails?.alias}
                        headerSize="big"
                        heroLabel={
                            accountType === "Portfolio" ? "Portfolio value:" : "Account value:"
                        }
                        heroContent={totalValue}
                        links={this.getLinks(targetAccount)}
                        action={
                            <ActionsContainer
                                isLoading={this.props.printState === PRINT_STATE.PRINTING}
                                data-testid="actions-container"
                            >
                                {this.getCSVGenerator({ transactions, funds })}
                                <PDFDownload
                                    onClick={this.handlePrint}
                                    data-testid="pdf-download-button"
                                />
                            </ActionsContainer>
                        }
                        content={this.getAccountSummary({ transactions, accountDetails })}
                        marginBottom={details.length ? "2rem" : "0"}
                    />
                    {details.map(det => (
                        <AccountDetail key={det.title} {...det} />
                    ))}
                </OverviewContainer>
                <Investments
                    isCombined={this.props.isCombined}
                    accountNum={this.props.params.accountNum}
                    funds={funds}
                    totalValue={totalValue}
                    pageBreakBefore={this.props.isCombined || !details.length ? "auto" : "always"}
                />
                {!this.props.isCombined && modelPortfolio && (
                    <PrintOnlyContainer>
                        <AccountDetail {...modelPortfolio} type="single" />
                    </PrintOnlyContainer>
                )}
            </ContentHolder>
        );
    }
}

export const pensionDisplayTypes = ["APP", "Pension (former PR)", "Pension"];
export const drawdownDisplayTypes = ["Unsecured APP", "Drawdown (former PR)", "Drawdown"];

export const Card = styled.div`
    margin-top: 1em;
    background: #fff;
`;

const OverviewContainer = styled.div`
    background-color: #fff;
    padding: 1rem;
    margin: 1rem 0;
    width: 100%;
`;

const PrintOnlyContainer = styled(OverviewContainer)`
    padding: 1rem 1rem 0 1rem;
    margin: 1rem 0 0 0;

    @media not print {
        display: none;
    }
`;

FolioDetailsComponent.defaultProps = {
    accounts: {},
    analysis: {},
    isCombined: false
};

FolioDetailsComponent.propTypes = {
    isCombined: PropTypes.bool,
    params: PropTypes.shape({
        accountNum: PropTypes.string
    }),
    accounts: PropTypes.object,
    analysis: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    clientId: PropTypes.number,
    modelPortfolioRequestStatus: requestObject,
    accountDetailsRequestStatus: requestObject,
    onPrint: PropTypes.func,
    printState: PropTypes.number,
    stateToPrint: PropTypes.shape({
        accounts: PropTypes.object,
        analysis: PropTypes.object,
        requests: PropTypes.object,
        theme: PropTypes.object,
        user: PropTypes.object
    })
};

export const FolioDetails = PDFWrapper(FolioDetailsComponent, "client-portal");
